import { orderBy, uniq, uniqBy } from "lodash";
import {
  FDADesignations,
  IndicationApprovalStageType,
  DisplayedIndicationApprovalStageType,
  DisplayedStages,
  SubmissionTypes,
} from "../typescript/enums/data";
import { context, data } from "../typescript/interfaces";
import { sort } from "../functions";

export const usaClinicalPipelineTearsheets = (data: data.ClinicalPipelineData[]): context.Data[] => {
  const normalized: context.Data[] = data.map((row: data.ClinicalPipelineData) => {
    const dataContext: context.Data = {
      id: row.id,
      approvedIndications: tearSheetIndicationApproved(row.tearSheetIndications),
      brandCompanies: tearSheetIdName(row.brandCompanies),
      brandName: row.drug.name,
      budgetImpacts: row.budgetImpacts.length === 0 ? [] : uniqBy(row.budgetImpacts, "id"),
      clinicalDevelopmentNotes: normalizeArray(row.rxStrategyPipelines, true, "rxStrategy", "clinicalDevelopmentNotes"),
      diseaseState: tearSheetIndicationIdName(row.tearSheetIndications, "diseaseState"),
      drugId: Number(row.drug.id),
      drugTypes: row.drug.isBiologic ? ["Biologics Only"] : ["Small Molecule Only"],
      hasPatentAnalysis: row.hasPatentAnalysis,
      drugURL: row.drug.url,
      exclusivities: row.exclusivities,
      fdaDesignations: tearSheetFDADesignations(row.tearSheetIndications, false, "fdaDesignations", "designation"),
      forecastGuid: row.id,
      financialForecasts: row.financialForecasts.length === 0 ? [] : uniqBy(row.financialForecasts, "id"),
      genericName: row.drug.genericName,
      hasDrugPipelineReport: hasPipelineReports(row),
      hasHighImpactTag: hasHighImpactTag(row),
      indicationApprovalStatus: sort.approvalStatus(indicationApprovalStatus(row.tearSheetIndications)),
      isBiologic: row.drug.isBiologic,
      mechanismsOfAction: tearSheetIdName(row.drug.mechanismsOfAction, true),
      notesAndCommentary: row.rxStrategyPipelines,
      otherAttributes: tearSheetIdName(row.otherAttributes),
      otherPipelineNames: row.otherPipelineNames,
      routesOfAdministration: tearSheetIdName(row.routesOfAdministration),
      rxStrategy: row.rxStrategyPipelines.map((strategy) => strategy.rxStrategy).filter(Boolean),
      stages: stages(row.tearSheetIndications),
      submissionTypes: submissionTypes(row.tearSheetIndications),
      tags: tags(row),
      tearSheetIndications: tearSheetIndicationIdName(row.tearSheetIndications, "indication"),
      tearSheetURL: row.url,
      therapeuticAreaTags: therapeuticAreaIdNameSlug(row.therapeuticAreaTags),
      isComplexMolecule:
        complexMoleculeForms(row.complexMolecule).length > 0 ||
        likelyComplexMoleculeForms(row.complexMolecule).length > 0,
      complexForms: complexMoleculeForms(row.complexMolecule),
      likelyComplexForms: likelyComplexMoleculeForms(row.complexMolecule),
      title: row.title,
      overflow: {
        brandCompanies: overflowDetails(row, "brandCompanies"),
        indicationApprovalStatus: overflowDetails(row, "indicationApprovalStatus"),
      },
    };
    return dataContext;
  });
  return normalized;
};

const overflowDetails = (row: data.ClinicalPipelineData, type: string): data.OverflowDetails => {
  const rowsVisible = 2;
  let numberOfItems: number;

  const calculateStagesLength = (row: data.ClinicalPipelineData): number => {
    const displayedStages = indicationApprovalStatus(row.tearSheetIndications).filter((tsi) => {
      return DisplayedIndicationApprovalStageType[tsi.stage || ""] === tsi.stage;
    });
    const sorted = sort.approvalStatus(displayedStages);
    const hasDate = sorted[0]?.date !== null;
    const displayDate =
      sorted[0]?.stage === IndicationApprovalStageType.CRL || sorted[0]?.stage === IndicationApprovalStageType.Pending;

    return hasDate && displayDate ? displayedStages.length + 1 : displayedStages.length;
  };

  switch (type) {
    case "brandCompanies":
      numberOfItems = tearSheetIdName(row.brandCompanies).length;
      break;
    default:
      numberOfItems = calculateStagesLength(row);
      break;
  }

  const isExpandable = numberOfItems > rowsVisible;

  return {
    isExpandable,
    hiddenRows: isExpandable ? numberOfItems - rowsVisible : 0,
  };
};

export const drugPopup = (drug: data.Drug) => {
  const approvedIndications: data.TearSheetIndication[] = [];
  const clinicalIndications: data.TearSheetIndication[] = [];

  for (const tearSheet of drug.tearSheets) {
    for (let indication of tearSheet.tearSheetIndications) {
      const stageStr = indication.status?.stage || "";
      const stageEnum = IndicationApprovalStageType[stageStr] || null;

      if (indication.status) {
        indication = {
          ...indication,
          status: {
            ...indication["status"],
            stage: stageEnum,
          },
        };
      }

      if (stageEnum === IndicationApprovalStageType.Approved) {
        approvedIndications.push(indication);
      } else {
        clinicalIndications.push(indication);
      }
    }
  }

  return {
    approvedIndications,
    clinicalIndications,
  };
};

const tearSheetIdName = (arr: Array<data.IdName>, isSorted = false) => {
  const arrayOfObjects = arr.map(({ id, name }) => ({
    id: Number(id),
    name,
  }));

  if (isSorted) return orderBy(uniqBy(arrayOfObjects, "id"), "name");
  return uniqBy(arrayOfObjects, "id");
};

const complexMoleculeForms = (data: data.ComplexMolecule | null) => {
  if (!data || !(data.reviewed || data.status === "Reviewed")) return [];
  return data.complexFormDescription
    .filter((x) => x.complexForm !== "TBD" && x.complexForm !== "None")
    .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
};

const likelyComplexMoleculeForms = (data: data.ComplexMolecule | null) => {
  if (!data || !(data.reviewed || data.status === "Reviewed")) return [];
  return data.likelyComplexFormDescription
    .filter((x) => x.complexForm !== "TBD" && x.complexForm !== "None")
    .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
};

const therapeuticAreaIdNameSlug = (arr: Array<data.TherapeuticArea>) => {
  return arr.map(({ id, name, slug }) => ({ id: Number(id), name, slug }));
};

const tearSheetIndicationIdName = (arr: Array<data.TearSheetIndication>, subValue: string) => {
  const arrayOfObjects = arr.map((item) => ({
    id: Number(item[subValue].id),
    name: item[subValue].name,
  }));

  return uniqBy(arrayOfObjects, "id");
};

const normalizeArray = (arr: Array<any>, isUniq: boolean, value: string, subValue?: string): Array<any> => {
  if (arr.length === 0) return [];

  const normalized = arr
    .map((key) => (subValue ? key[value][subValue] : key[value]))
    .filter(Boolean)
    .flat();

  if (isUniq) {
    return uniq(normalized);
  } else {
    return normalized;
  }
};

const tearSheetFDADesignations = (outer: Array<any>, isUniq: boolean, value: string, subValue: string): Array<any> => {
  if (outer.length === 0) return [];

  const normalized = outer
    .map((o) => normalizeArray(o[value], isUniq, subValue))
    .filter(Boolean)
    .flat();

  const output = normalized.map((designation) => FDADesignations[designation]);

  if (isUniq) {
    return uniq(output);
  } else {
    return output;
  }
};

const tearSheetIndicationApproved = (arr: Array<data.TearSheetIndication>) => {
  if (arr.length === 0) return [];

  const output = arr
    .filter((tsi) => tsi.status?.stage === IndicationApprovalStageType.Approved)
    .filter(Boolean)
    .flat();

  return output;
};

const indicationApprovalStatus = (
  tearSheetIndications: Array<data.TearSheetIndication>
): data.IndicationApprovalStatus[] => {
  const approvals = tearSheetIndications.map((tsi: data.TearSheetIndication): data.IndicationApprovalStatus => {
    const stageStr = tsi.status?.stage || "";
    const stage = IndicationApprovalStageType[stageStr] || null;
    const date = tsi.status?.date?.value || null;
    const primaryCompletionDate = {
      date: tsi.primaryCompletionDate?.date || "",
      isActual: tsi.primaryCompletionDate?.isActual || false,
    };
    const studyCompletionDate = {
      date: tsi.studyCompletionDate?.date || "",
      isActual: tsi.studyCompletionDate?.isActual || false,
    };
    const indication = tsi.indication.name;

    const noRecentDevelopment = tsi.fdaDesignations
      .map((x) => (x as any).designation)
      .includes("No_Recent_Development");

    if (noRecentDevelopment) {
      console.log(`No recent development for ${tsi.indication.name} (${tsi.status?.stage})`);
    }

    return { stage, date, indication, primaryCompletionDate, studyCompletionDate, noRecentDevelopment };
  });

  const uniqApprovals = uniqBy(approvals, (elem) => [elem.indication, elem.stage].join());
  return uniqApprovals;
};

const stages = (tearSheetIndications: Array<data.TearSheetIndication>): IndicationApprovalStageType[] => {
  const stages = tearSheetIndications.map((tsi: data.TearSheetIndication) => {
    if (tsi.status?.stage === IndicationApprovalStageType.CRL) {
      return "Complete Response Letter";
    } else {
      return IndicationApprovalStageType[tsi.status?.stage || ""];
    }
  });
  return uniq(stages);
};

const hasPipelineReports = (row: data.ClinicalPipelineData): boolean => {
  return row.rxStrategyPipelines.map((pipeline) => pipeline.rxStrategy).length > 0;
};

const hasHighImpactTag = (row: data.ClinicalPipelineData): boolean => {
  return row.drug.hiddenTags.some((x) => x.name == "High Impact Pipeline Report");
};

const tags = (row: data.ClinicalPipelineData): (data.OtherTag | FDADesignations)[] => {
  const otherAttributes = uniqBy(
    row.otherAttributes.map((attribute) => attribute),
    "id"
  )
    .map((uniqAttrs) => uniqAttrs.name)
    .flat();

  const fdaDesignations = tearSheetFDADesignations(row.tearSheetIndications, false, "fdaDesignations", "designation");

  return uniq([...otherAttributes, ...fdaDesignations]);
};

const submissionTypes = (tearSheetIndications: Array<data.TearSheetIndication>) => {
  return tearSheetIndications
    .map((tsi) => {
      if (Object.keys(DisplayedStages).includes(tsi.status?.stage || "")) {
        return SubmissionTypes[tsi.submissionType || ""];
      }
    })
    .filter(Boolean);
};
